import { useMemo } from "react";

import { useDebounce } from "use-debounce";

import { AssetsIcon } from "@hightouch/htui";

import { matchName } from "src/components/commandbar/match";
import {
  useCommandBarQuery,
  useRegisterCommandResults,
} from "src/components/commandbar/results-context";
import {
  type CommandResult,
  type CommandScope,
  commandScopeLabel,
  scopeLabelMatchStart,
} from "src/components/commandbar/types";
import {
  LIST_QUERY_OPTIONS,
  QUERY_RETRIES,
  SCOPED_LIST_LIMIT,
  SEARCH_DEBOUNCE_MS,
  SEARCH_LIMIT,
  SEARCH_MIN_LENGTH,
  useHeldWhileViewing,
} from "src/components/commandbar/use-command-index";
import { useHasProductTabs } from "src/components/layout/nav/sections/hooks/projects";
import { useReachableNav } from "src/components/layout/nav/use-reachable-nav";
import {
  type CommandBarLibraryFilesQuery,
  useCommandBarLibraryFilesQuery,
  useCommandBarLibraryPresenceQuery,
} from "src/graphql";
import { PROJECTS_PATHS, libraryFilePath } from "src/pages/projects/constants";
import { fileTypes } from "src/pages/projects/library/files/file-type";

const LIBRARY = "library";
// The Library's own nav icon.
export const LIBRARY_SCOPE: CommandScope = {
  type: LIBRARY,
  icon: AssetsIcon,
  href: PROJECTS_PATHS.library,
};
const CONTENT_SCOPE: CommandScope = { ...LIBRARY_SCOPE, noun: "content" };

// The product nav calls the Library "Content", so the palette does too.
export const useLibraryScope = () =>
  useHasProductTabs() ? CONTENT_SCOPE : LIBRARY_SCOPE;

// The nav reaches the Library from the product tabs, or from the Projects entry once a viewer goes back to the classic nav.
export const useIsLibraryReachable = () =>
  useReachableNav().hrefs.has(PROJECTS_PATHS.library);

// The server matches the search as one substring, so it gets the longest word and the palette matches every word on what comes back.
export const librarySearchTerm = (term: string) => {
  const longest = term
    .split(/\s+/)
    .reduce((best, word) => (word.length > best.length ? word : best));
  return longest.length >= SEARCH_MIN_LENGTH ? longest : term;
};

// A multi-word search fetches a scope's worth, since the server matched only one word, and keeps the first that match them all.
export const toLibrarySearchResults =
  (term: string, limit: number) => (data: CommandBarLibraryFilesQuery) =>
    toLibraryResults(data)
      .filter((result) => matchName(result.name, term))
      .slice(0, limit);

// Same-named files are told apart by type, then by when they were edited, only when the shell's disambiguator needs it.
export const toLibraryResults = (
  data: CommandBarLibraryFilesQuery,
): CommandResult[] =>
  data.listAmpFiles.items.flatMap((file) =>
    // A type this app doesn't know yet has no icon to show.
    file.name && Object.hasOwn(fileTypes, file.type)
      ? [
          {
            id: file.id,
            type: LIBRARY,
            name: file.name,
            icon: fileTypes[file.type].icon,
            href: libraryFilePath({
              id: file.id,
              canvasId: file.canvas?.canvasId ?? null,
            }),
            updatedAt: file.updatedAt,
            detail: fileTypes[file.type].label,
            isEditedLabelled: true,
          },
        ]
      : [],
  );

// Library files, searched on the server like syncs and ads, with a scope that lists the last updated files.
export const useLibrarySource = () => {
  const isReachable = useIsLibraryReachable();
  const libraryScope = useLibraryScope();
  const { search, scope } = useCommandBarQuery();
  const query = search.trim();
  const [term] = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const view = `${query}\n${scope?.type ?? ""}`;
  const isScoped = scope?.type === LIBRARY;

  const presence = useCommandBarLibraryPresenceQuery(undefined, {
    ...LIST_QUERY_OPTIONS,
    enabled: isReachable,
    select: (data) => data.ampLibraryCounts.all > 0,
  });
  const hasFiles = useHeldWhileViewing(presence, view) ?? false;

  const isSearchable =
    isReachable && (!scope || isScoped) && query.length >= SEARCH_MIN_LENGTH;
  const serverSearch = useCommandBarLibraryFilesQuery(
    {
      search: librarySearchTerm(term),
      limit: isScoped || /\s/.test(term) ? SCOPED_LIST_LIMIT : SEARCH_LIMIT,
    },
    {
      retry: QUERY_RETRIES,
      enabled: isSearchable && term.length >= SEARCH_MIN_LENGTH,
      keepPreviousData: true,
      select: toLibrarySearchResults(
        term,
        isScoped ? SCOPED_LIST_LIMIT : SEARCH_LIMIT,
      ),
    },
  );

  // Fetched once the query offers the scope too, so Tab lists the files at once.
  const isOffered =
    query.length >= SEARCH_MIN_LENGTH &&
    scopeLabelMatchStart(commandScopeLabel(libraryScope), query) !== -1;
  const listing = useCommandBarLibraryFilesQuery(
    { limit: SCOPED_LIST_LIMIT },
    {
      ...LIST_QUERY_OPTIONS,
      enabled: isReachable && (isScoped || (!scope && hasFiles && isOffered)),
      select: toLibraryResults,
    },
  );
  const listingData = useHeldWhileViewing(listing, view);

  const failed = [
    ...(isSearchable && term === query && serverSearch.isError
      ? [serverSearch]
      : []),
    ...(isReachable && isScoped && listing.isError ? [listing] : []),
  ];
  // keepPreviousData hands back the last page as already selected, so after leaving the scope a 50-file page is cut to an unscoped one here.
  // Memoized because the results register with the palette, which re-registers on every new array.
  const serverRows = serverSearch.data;
  const unscopedRows = useMemo(
    () =>
      isSearchable && !isScoped && serverRows
        ? serverRows.slice(0, SEARCH_LIMIT)
        : undefined,
    [isSearchable, isScoped, serverRows],
  );
  const searchResults = isSearchable
    ? ((isScoped ? serverRows : unscopedRows) ?? [])
    : [];
  useRegisterCommandResults({
    id: "library-search",
    deferred: true,
    scopes: isReachable && hasFiles ? [libraryScope] : [],
    results:
      isReachable && isScoped
        ? [...(listingData ?? []), ...searchResults]
        : searchResults,
    isLoading:
      (isReachable && isScoped && listing.isLoading) ||
      (isSearchable && (term !== query || serverSearch.isFetching)) ||
      failed.some((failedQuery) => failedQuery.isFetching),
    isError: failed.length > 0,
    onRetry: () => {
      for (const failedQuery of failed) {
        void failedQuery.refetch();
      }
    },
  });
};
